import React, { useState, useEffect } from 'react';
import moment from 'moment';
import SideBarContainer from 'Containers/SideBarContainer';
import { ImageOutlined, Edit } from '@mui/icons-material/';
import { Button, CircularProgress } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { LOCALES } from 'Constants/General';
import { userLessonSave, userLessonPublish } from 'Api/attendance';
import { s3 } from 'Services/AWS/s3';
import Alert from 'Components/Alert';
import BasicTable from '../../Components/BasicTable';
import Search from '../../Components/Search';
import useTranslation from '../../Hooks/useTranslation';
import DropDown from '../../Components/DropDown';
import useDropDown from './Hooks/useDropdown';
import useFetch from './Hooks/useFetch';
import useComponent from './Hooks/useComponent';
import useSessionComponent from './Hooks/useSessionComponent';
import {
  Header,
  HeaderContent,
  HeaderIcon,
  HeaderInfo,
  EvaluationTitle,
  EvaluationSubTitle,
  SearchContainer,
  Content,
  FiltersContainer,
  DropDownsContainer,
  publishButton,
  ButtonContainer,
  saveButton,
  thumbnail,
  LocationInfo,
  EditEvaluationIcon,
  disabledButton,
  disabledPublishButton,
} from './style';

export default function Attendance() {
  const location = useLocation();
  const session = location?.state?.dateInfo;
  const courseId = location?.state?.courseId;
  const courseThumbnail = location?.state?.courseThumbnail;
  const [isLoading, setIsLoading] = useState(true);
  const [userAttendance, setUserAttendance] = useState([]);

  const { t2 } = useTranslation('Attendance');
  const {
    employees,
    initialAttendanceState,
    setInitialAttendanceState,
    departments,
    terminals,
    jobTitles,
    unpublishedAttendance,
  } = useFetch(
    {
      lessonId: session.id,
      courseId,
    },
    setIsLoading
  );
  const { setSearch, setTerminal, setDepartment, setJobTitle, employeesFilters } = useDropDown({
    employees,
  });
  const titles = [t2('Terminal'), t2('Department'), t2('JobTitle')];
  const options = [terminals, departments, jobTitles];
  const methods = [setTerminal, setDepartment, setJobTitle];
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertError, setAlertError] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const { date, address, meetUrl, LessonType } = session;
  const sessionType = LessonType?.name;
  const dateObject = new Date(`${date} 00:00`);
  const dayNum = dateObject.getDate();
  const month = dateObject.toLocaleDateString(LOCALES, { month: 'long' });
  const year = dateObject.getFullYear();
  const time = session.time === null ? t2('AllDay') : moment(session.time, 'HH:mm:ss').format('hh:mm A');
  const [fetchedImage, setFetchedImage] = useState(null);
  const { sessionComponent } = useSessionComponent({
    sessionType,
    meetUrl,
    address,
  });

  const [savedAttendance, setSavedAttendance] = useState(false);

  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (courseThumbnail) {
      s3.getSignedUrl(courseThumbnail).then(response => {
        setFetchedImage(response?.signedUrl);
      });
    }

    if (unpublishedAttendance.includes(true)) {
      setSavedAttendance(true);
    }
  }, [courseThumbnail, unpublishedAttendance]);

  const { thereIsComponent } = useComponent(userAttendance, setUserAttendance, initialAttendanceState, editing);

  const handleEdit = () => {
    setEditing(true);
    setSavedAttendance(false);
  };

  const handleSave = async () => {
    const response = await userLessonSave({
      lessonId: session.id,
      userAttendance,
    });

    if (response) {
      const temp = initialAttendanceState;
      userAttendance.forEach(({ id, attended, lessonIndex }) => {
        temp[lessonIndex] = { id, attended };
      });

      setInitialAttendanceState(temp);
      setUserAttendance([]);
      setAlertMessage(t2('AlertSaveSuccess'));
      setAlertOpen(true);
    } else {
      setAlertMessage(t2('AlertSaveError'));
      setAlertError(true);
      setAlertOpen(true);
    }

    setSavedAttendance(true);
    setEditing(false);
  };

  const handlePublish = async () => {
    const response = await userLessonPublish({ lessonId: session.id });
    if (response.status === 'success') {
      setAlertMessage(t2('AlertSendAttendanceSuccess'));
      setAlertOpen(true);
    } else {
      setAlertMessage(t2('AlertSendAttendanceError'));
      setAlertError(true);
      setAlertOpen(true);
    }

    setSavedAttendance(false);
  };

  let imageContent;
  if (!courseThumbnail) {
    imageContent = <ImageOutlined />;
  }

  if (courseThumbnail && !fetchedImage) {
    imageContent = <CircularProgress color="success" />;
  }

  if (fetchedImage) {
    imageContent = <img src={fetchedImage} style={thumbnail} />;
  }

  return (
    <SideBarContainer>
      <Header>
        <HeaderContent>
          <HeaderIcon>{imageContent}</HeaderIcon>
          <HeaderInfo>
            <EvaluationTitle>
              {t2('AttendanceCall')} - {session.name}
            </EvaluationTitle>
            <EvaluationSubTitle>
              <div>{`${dayNum} ${t2('Of')} ${month} ${t2('of')} ${year} - ${time}`}</div>
              <LocationInfo>{sessionComponent}</LocationInfo>
            </EvaluationSubTitle>
          </HeaderInfo>
          {!editing && (
            <EditEvaluationIcon onClick={handleEdit}>
              <Edit />
            </EditEvaluationIcon>
          )}
          {editing && (
            <ButtonContainer>
              <Button
                variant="contained"
                color="success"
                onClick={handleSave}
                style={savedAttendance ? disabledButton : saveButton}
                disabled={savedAttendance}
              >
                {t2('SaveChanges')}
              </Button>
            </ButtonContainer>
          )}
          {!editing && savedAttendance && (
            <ButtonContainer>
              <Button
                variant="contained"
                color="success"
                onClick={handlePublish}
                style={!savedAttendance ? disabledPublishButton : publishButton}
                disabled={!savedAttendance}
              >
                {t2('SendAttendance')}
              </Button>
            </ButtonContainer>
          )}
        </HeaderContent>
      </Header>
      <br />
      <br />
      <Content>
        <FiltersContainer>
          <SearchContainer>
            <Search setData={setSearch} />
          </SearchContainer>
          <DropDownsContainer>
            {titles.map((title, index) => (
              <DropDown key={title + index} title={title} options={options[index]} setData={methods[index]} />
            ))}
          </DropDownsContainer>
        </FiltersContainer>
        <br />
        <BasicTable
          information={employeesFilters}
          thereIsComponent={thereIsComponent}
          headers={[t2('Name'), t2('Department'), t2('JobTitle'), t2('Terminal'), t2('Present')]}
          limit={5}
          isLoading={isLoading}
        />
      </Content>
      <Alert message={alertMessage} open={alertOpen} setOpen={setAlertOpen} error={alertError} />
    </SideBarContainer>
  );
}
